import React from "react";
import {
  Box,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

/**
 * Chat Categories Popup Component
 * 
 * This component displays a dialog with available chat categories for a business location.
 * Users can select a category to create a nested chat conversation.
 * Categories that already have sub-conversations created and the main conversation category are filtered out.
 * 
 * Props:
 * - open: boolean - Controls whether the dialog is open
 * - onClose: function - Callback to close the dialog
 * - chatCategories: object - API response containing chat categories data
 * - chatCategoriesLoading: boolean - Loading state for categories
 * - onCategorySelect: function - Callback when a category is selected
 * - subConversations: array - Existing sub-conversations to filter out from available categories
 * - conversationInfo: object - Main conversation info to filter out its category
 */
const ChatCategoriesPopup = ({
  open,
  onClose,
  chatCategories,
  chatCategoriesLoading,
  onCategorySelect,
  subConversations = [],
  conversationInfo = null,
}) => {
  // Filter out categories that already have sub-conversations created and the main conversation category
  // Compare category names/IDs with existing sub-conversation category names/IDs and main conversation category
  const getAvailableCategories = () => {
    if (!Array.isArray(chatCategories?.data?.data?.chatCategories)) {
      return [];
    }

    const allCategories = chatCategories.data.data.chatCategories;

    // Get list of category names/IDs that already have sub-conversations
    const existingCategoryIds = subConversations.map(subConv => 
      subConv.categoryId?._id || subConv.categoryId
    ).filter(Boolean);
    
    const existingCategoryNames = subConversations.map(subConv => 
      subConv.categoryId?.name?.toLowerCase() || subConv.subject?.toLowerCase()
    ).filter(Boolean);

    // Get main conversation category info to exclude it
    const mainConversationCategoryId = conversationInfo?.chatCategory?._id || conversationInfo?.chatCategory?.categoryId;
    const mainConversationCategoryName = conversationInfo?.chatCategory?.name?.toLowerCase();



    // Filter out categories that already exist as sub-conversations or are the main conversation category
    return allCategories.filter(category => {


      // Check by category ID - exclude if already exists as sub-conversation
      if (category._id && existingCategoryIds.includes(category._id)) {
        return false;
      }
      
      // Check by category name - exclude if already exists as sub-conversation (case-insensitive)
      if (category.name && existingCategoryNames.includes(category.name.toLowerCase())) {
        return false;
      }

      // Check if this is the main conversation's category - exclude it
      if (mainConversationCategoryId && category._id === mainConversationCategoryId) {
        return false;
      }

      // Check by main conversation category name (case-insensitive)
      if (mainConversationCategoryName && category.name && 
          category.name.toLowerCase() === mainConversationCategoryName) {
        return false;
      }
      
      return true;
    });
  };

  const availableCategories = getAvailableCategories();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "#000000",
            fontSize: "1.125rem",
          }}
        >
          select message subject
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "#000000" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, pb: 2 }}>
        {/* Average Response Time */}
        <Typography
          variant="body2"
          sx={{
            color: "#7C3AED",
            fontSize: "0.875rem",
            mb: 2,
            fontWeight: 500,
          }}
        >
          avg response time : 30 sec
        </Typography>

        {/* Chat Categories List */}
        {chatCategoriesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {availableCategories.length > 0 ? (
              availableCategories.map((category, index) => (
                <ListItem
                  key={category._id || index}
                  button
                  onClick={() => onCategorySelect(category)}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    "&:hover": {
                      backgroundColor: "#F3F4F6",
                    },
                    cursor: "pointer",
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        backgroundColor: "#7C3AED",
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#FFFFFF",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                        }}
                      >
                        {category.name?.charAt(0)?.toUpperCase() || "C"}
                      </Typography>
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#000000",
                          fontSize: "0.875rem",
                          fontWeight: 500,
                        }}
                      >
                        {category.name || "category"}
                      </Typography>
                    }
                  />
                </ListItem>
              ))
            ) : (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#6B7280",
                    fontSize: "0.875rem",
                    textTransform: "lowercase",
                  }}
                >
                  {subConversations.length > 0 
                    ? "all categories already have conversations created"
                    : "no chat categories available"
                  }
                </Typography>
              </Box>
            )}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChatCategoriesPopup;
